// src/features/settings/api/useIntegrations.ts
// Integration & Webhook hooks
// All endpoints verified against openapi.json (integration-service)
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Integration {
  id: number
  name: string
  integration_type: string
  category: string
  description?: string
  config: Record<string, any>
  is_active: boolean
  last_sync?: string
  created_at: string
}

export interface Webhook {
  id: number
  name: string
  url: string
  description?: string
  events: string[]
  secret?: string
  is_active: boolean
  failure_count: number
  created_at: string
}

export interface IntegrationCreate {
  integration_type: string
  name: string
  description?: string
  config: Record<string, any>
  is_active?: boolean
}

export interface WebhookCreate {
  name: string
  url: string
  description?: string
  events: string[]
  secret?: string
  is_active?: boolean
}

export interface IntegrationTypesResponse {
  integration_types: Record <
    string,
    {
      name: string
      description?: string
      category: string
    }
  >
  categories: Record<string, string>
}

export interface CreateIntegrationPayload {
  name: string
  integration_type: string
  category?: string
  description?: string
  config: Record<string, any>
  is_active?: boolean
}

// ---------------------------------------------------------------------------
// Integrations
// ---------------------------------------------------------------------------

export const useIntegrations = () =>
  useQuery<Integration[]>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const { data } = await apiClient.get('/api/integrations')
      return data
    },
  })

export const useIntegrationTypes = () =>
  useQuery<IntegrationTypesResponse>({
    queryKey: ['integrationTypes'],
    queryFn: async () => {
      const { data } = await apiClient.get('/api/integrations/types')
      return data
    },
  })

export const useCreateIntegration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateIntegrationPayload) => {
      const { data } = await apiClient.post('/api/integrations', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

export const useUpdateIntegration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: Partial<IntegrationCreate>
    }) => {
      const { data: res } = await apiClient.put(
        `/api/integrations/${id}`,
        data
      )
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

export const useDeleteIntegration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/integrations/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const useWebhooks = () =>
  useQuery<Webhook[]>({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const { data } = await apiClient.get('/api/webhooks')
      return data
    },
  })

export const useCreateWebhook = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WebhookCreate) => {
      const { data } = await apiClient.post('/api/webhooks', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })
}

export const useUpdateWebhook = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: {
        name?: string
        url?: string
        description?: string
        events?: string[]
        is_active?: boolean
      }
    }) => {
      const res = await apiClient.put(`/api/webhooks/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })
}

export const useDeleteWebhook = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/webhooks/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })
}